import React, { useState, useMemo } from 'react';
import { Search, Filter, Clock, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { courses } from '../data/mock';

const CoursesSection = ({ showLoadMore = true, showAllByDefault = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [showAll, setShowAll] = useState(showAllByDefault);
  const navigate = useNavigate();

  const filteredCourses = useMemo(() => {
    let filtered = courses;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(course => 
        course.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply category filter
    if (activeFilter !== 'all') {
      if (activeFilter === 'certification') {
        filtered = filtered.filter(course => course.category === 'studyNotes' || course.category === 'practiceExams');
      } else if (activeFilter === 'projects') {
        filtered = filtered.filter(course => course.category === 'projects');
      } else {
        filtered = filtered.filter(course => course.category === activeFilter);
      }
    }

    return filtered;
  }, [searchTerm, activeFilter]);

  const displayedCourses = showAll ? filteredCourses : filteredCourses.slice(0, 4);

  const handleCourseClick = (course) => {
    // Scroll to top before navigation
    window.scrollTo(0, 0);
    
    // Navigate to specific course pages that exist
    if (course.id === 1 && course.title === "AWS Interview Mastery") {
      navigate('/courses/aws-interview-mastery');
    } else if (course.id === 2 && course.title === "The Part-Time Tech Career YouTuber") {
      navigate('/courses/part-time-tech-youtuber');
    } else if (course.id === 3 && course.title === "Study Notes: AWS Certified AI Practitioner") {
      navigate('/courses/study-notes-ai-practitioner');
    } else if (course.id === 4 && course.title === "Study Notes: AWS Certified Cloud Practitioner") {
      navigate('/courses/study-notes-cloud-practitioner');
    } else if (course.id === 5 && course.title === "Study Notes: AWS Certified Solutions Architect - Associate") {
      navigate('/courses/study-notes-solutions-architect');
    } else if (course.id === 14 && course.title === "Study Notes: AWS Certified Developer - Associate") {
      navigate('/courses/study-notes-developer-associate');
    } else if (course.id === 6 && course.title === "5 AWS Cloud Projects to Become a Cloud Support Engineer") {
      navigate('/courses/cloud-support-engineer-projects');
    } else if (course.id === 7 && course.title === "5 AWS Cloud Projects to Become a Solutions Architect") {
      navigate('/courses/solutions-architect-projects');
    } else if (course.id === 8 && course.title === "Build: 5 Advanced AWS Cloud Projects") {
      navigate('/courses/advanced-aws-projects');
    } else if (course.id === 9 && course.title === "Build: 5 Beginner AWS Cloud Projects") {
      navigate('/courses/beginner-aws-projects');
    } else if (course.id === 10 && course.title === "Build: 5 Intermediate AWS Cloud Projects") {
      navigate('/courses/intermediate-aws-projects');
    } else if (course.id === 11 && course.title === "Free Project: AWS Cloud Fun Facts Generator") {
      navigate('/courses/free-cloud-fun-facts');
    } else if (course.id === 12 && course.title === "AWS Certified Cloud Practitioner Practice Tests") {
      navigate('/courses/practice-exams-cloud-practitioner');
    } else if (course.id === 13 && course.title === "AWS Certified AI Practitioner Practice Tests") {
      navigate('/courses/practice-exams-ai-practitioner');
    } else if (course.id === 15 && course.title === "AWS Certified Solutions Architect - Associate Practice Tests") {
      navigate('/courses/practice-exams-solutions-architect');
    } else if (course.id === 16 && course.title === "5 AWS Projects to Become a Cloud AI/ML Engineer") {
      navigate('/courses/ai-ml-engineer-projects');
    } else if (course.id === 17 && course.title === "5 AWS Projects to Become a Cloud Security Engineer") {
      navigate('/courses/cloud-security-engineer-projects');
    } else if (course.id === 18 && course.title === "5 AWS Projects to Become a Cloud Engineer") {
      navigate('/courses/cloud-engineer-projects');
    } else if (course.id === 19 && course.title === "5 AWS Cloud Projects to Become a DevOps Engineer") {
      navigate('/courses/devops-engineer-projects');
    } else if (course.id === 20 && course.title === "Study Notes: AWS Certified CloudOps Engineer - Associate") {
      navigate('/courses/study-notes-cloudops');
    } else if (course.id === 21 && course.title === "AWS Certified Developer - Associate Practice Tests") {
      navigate('/courses/practice-exams-developer-associate');
    } else if (course.id === 22 && course.title === "5 Beginner Azure Cloud Projects") {
      navigate('/courses/azure-beginner-projects');
    } else if (course.id === 23 && course.title === "Study Notes: Azure Fundamentals") {
      navigate('/courses/study-notes-azure-fundamentals');
    } else if (course.id === 24 && course.title === "Study Notes: Azure Administrator Associate") {
      navigate('/courses/study-notes-azure-administrator');
    } else if (course.id === 25 && course.title === "5 Intermediate Azure Cloud Projects") {
      navigate('/courses/azure-intermediate-projects');
    }
    // Add more course-specific routes as needed
  };

  return (
    <section className="py-20 bg-white">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="heading-large text-gray-900 mb-4">
            Courses
          </h2>
          <p className="body-large text-gray-600 max-w-2xl mx-auto">
            Explore our Extensive Course Library
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-12">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Search Bar */}
            <div className="relative w-full lg:w-96">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white border border-blue-200 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2">
              <Filter className="text-gray-400" size={20} />
              <button
                onClick={() => setActiveFilter('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeFilter === 'all'
                    ? 'bg-gradient-to-r from-blue-500 to-teal-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-blue-50 border border-blue-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setActiveFilter('certification')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeFilter === 'certification'
                    ? 'bg-gradient-to-r from-blue-500 to-teal-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-blue-50 border border-blue-200'
                }`}
              >
                Certification
              </button>
              <button
                onClick={() => setActiveFilter('career')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeFilter === 'career'
                    ? 'bg-gradient-to-r from-blue-500 to-teal-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-blue-50 border border-blue-200'
                }`}
              >
                Career
              </button>
              <button
                onClick={() => setActiveFilter('projects')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeFilter === 'projects'
                    ? 'bg-gradient-to-r from-blue-500 to-teal-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-blue-50 border border-blue-200'
                }`}
              >
                Projects
              </button>
            </div>
          </div>
        </div>

        {/* Course Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {displayedCourses.map((course) => (
            <div key={course.id} className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 hover-lift overflow-hidden border border-blue-100 flex flex-col h-full cursor-pointer" onClick={() => handleCourseClick(course)}>
              <div className="relative">
                {course.comingSoon ? (
                  <div className="w-full h-40 bg-gray-300 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-gray-600 font-semibold text-lg mb-2">COMING SOON</div>
                      <div className="text-gray-500 text-sm">Stay Tuned</div>
                    </div>
                  </div>
                ) : (
                  <img 
                    src={course.image} 
                    alt={course.title}
                    className="w-full h-40 object-contain bg-gray-50"
                  />
                )}
                {course.enrolled && (
                  <div className="absolute top-3 left-3 bg-teal-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                    Enrolled
                  </div>
                )}
                {course.comingSoon && (
                  <div className="absolute top-3 right-3 bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                    Coming Soon
                  </div>
                )}
              </div>
              
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-sm font-semibold text-gray-900 mb-6 leading-tight min-h-[2rem] line-clamp-1">
                  {course.title}
                </h3>

                <div className="flex items-center justify-between mt-auto">
                  <div className="text-2xl font-bold text-gray-900">
                    {course.price === 0 ? 'Free' : `$${course.price}`}
                  </div>
                  <button 
                    className={`text-sm px-4 py-2 ${
                      course.comingSoon 
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed rounded-lg' 
                        : 'btn-outline-gradient'
                    }`}
                    disabled={course.comingSoon}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!course.comingSoon) {
                        handleCourseClick(course);
                      }
                    }}
                  >
                    {course.comingSoon ? 'Coming Soon' : course.enrolled ? 'Continue' : 'More info'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Load More Button */}
        {showLoadMore && !showAll && filteredCourses.length > 4 && (
          <div className="text-center">
            <button 
              onClick={() => setShowAll(true)}
              className="btn-secondary px-8 py-3"
            >
              Load more courses
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default CoursesSection;